import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MessageCircle, FileSignature, ShieldAlert } from "lucide-react";
import { api, fmtDateTime } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export function NotificationBell() {
  const [data, setData] = useState({ unread: 0, items: [] });
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const load = useCallback(() => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const go = async (n) => {
    await api.post(`/notifications/${n.id}/read`).catch(() => {});
    setOpen(false);
    load();
    if (n.type === "contract_converted") nav(`/trusts/${n.trust_id}`);
    else if (n.type === "contract_unsigned" || n.type === "contract_declined") nav(`/prospects/${n.prospect_id}/contract`);
    else if (n.trust_id) nav(`/trusts/${n.trust_id}?tab=whatsapp${n.subject_id ? `&subject=${n.subject_id}` : ""}`);
  };
  const readAll = async () => { await api.post("/notifications/read-all").catch(() => {}); load(); };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button className="relative h-9 w-9 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center hover:bg-slate-50 transition-colors" aria-label="Notifiche" data-testid="notification-bell">
          <Bell className="h-4 w-4 text-slate-700" />
          {data.unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold flex items-center justify-center" data-testid="notification-count">
              {data.unread > 99 ? "99+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-white" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <p className="font-heading font-semibold text-sm text-slate-900">Notifiche</p>
          {data.unread > 0 && <button className="text-xs text-sky-700 hover:underline" onClick={readAll} data-testid="notification-read-all">Segna tutte come lette</button>}
        </div>
        <ul className="max-h-96 overflow-y-auto divide-y divide-slate-100">
          {!data.items.length && <li className="px-4 py-8 text-center text-sm text-slate-400" data-testid="notification-empty">Nessuna notifica</li>}
          {data.items.map((n) => (
            <li key={n.id}>
              <button onClick={() => go(n)} className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-slate-50 ${n.read ? "" : "bg-sky-50/60"}`} data-testid={`notification-item-${n.id}`}>
                {n.type === "antivirus_down" ? <ShieldAlert className="h-4 w-4 text-red-600 mt-0.5 shrink-0" /> : n.type?.startsWith("contract_") ? <FileSignature className="h-4 w-4 text-sky-700 mt-0.5 shrink-0" /> : <MessageCircle className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />}
                <span className="min-w-0">
                  <span className={`block text-sm ${n.read ? "text-slate-600" : "text-slate-900 font-medium"}`}>{n.title}</span>
                  <span className="block text-[11px] text-slate-400">{n.trust_name ? `${n.trust_name} · ` : ""}{fmtDateTime(n.created_at)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
